import { useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Modal } from './Modal';
import { Button } from './Button';
import { MachhaCharacter } from '../../lib/machhaAsset';
import { quotaApi } from '../../api/quota';
import type { QuotaType } from '../../types';
import styles from './QuotaLimitModal.module.css';

const QUOTA_LABELS: Record<QuotaType, string> = {
  events: 'events',
  photosPerEvent: 'photos for this event',
  albumsPerEvent: 'albums for this event',
  ftpAccountsPerEvent: 'FTP accounts for this event',
  liveAlbumsPerEvent: 'live albums for this event',
};

interface QuotaLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotaType: QuotaType;
  current: number;
  limit: number;
  eventId?: string;
}

export function QuotaLimitModal({ isOpen, onClose, quotaType, current, limit, eventId }: QuotaLimitModalProps) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await quotaApi.requestIncrease({ quotaType, reason: reason.trim() || undefined, eventId });
      setSubmitted(true);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset after animation
    setTimeout(() => { setSubmitted(false); setReason(''); }, 300);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={submitted ? 'Request Submitted' : 'Limit Reached'} size="sm">
      <div className={styles['content']}>
        {!submitted ? (
          <>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <MachhaCharacter state="Rate Limit" size={120} />
            </motion.div>

            <div>
              <p className={styles['heading']}>
                You've reached your {QUOTA_LABELS[quotaType]} limit
              </p>
              <p className={styles['subtext']}>
                {current} of {limit} used — this is a beta account limitation.
              </p>
            </div>

            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Tell us why you need more (optional)"
              maxLength={500}
              rows={3}
              className={styles['textarea']}
            />

            <div className={styles['button-row']}>
              <Button variant="ghost" onClick={handleClose} className={styles['btn-flex']}>
                Close
              </Button>
              <Button onClick={handleSubmit} loading={submitting} className={styles['btn-flex']}>
                Request Increase
              </Button>
            </div>
          </>
        ) : (
          <>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <MachhaCharacter state="Onboarding" part="face" size={100} />
            </motion.div>

            <div>
              <p className={styles['heading']}>
                Request submitted!
              </p>
              <p className={styles['subtext']}>
                We'll review your request and get back to you soon.
              </p>
            </div>

            <Button onClick={handleClose} className={styles['btn-full']}>
              Got it
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
}
